import { Injectable } from "@angular/core";
import { INDEXED_DB_NAME, INDEXED_DB_VERSION, LEGACY_UPLOAD_PROGRESS_STORE, OST_CREATION_IMAGE_STORE, OST_UPLOAD_STORES } from "./indexed-db.schema";

@Injectable({ providedIn: "root" })
export class IndexedDBDatabaseService {
  private databasePromise?: Promise<IDBDatabase>;

  openDatabase(): Promise<IDBDatabase> {
    if (!this.databasePromise) {
      this.databasePromise = this.openAndPrepareDatabase().catch((error: unknown) => {
        this.databasePromise = undefined;
        throw error;
      });
    }
    return this.databasePromise;
  }

  waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (reason: unknown) => void) => {
      transaction.oncomplete = (): void => resolve();
      transaction.onerror = (): void => reject(transaction.error ?? new Error("IndexedDB transaction failed."));
      transaction.onabort = (): void => reject(transaction.error ?? new Error("IndexedDB transaction was aborted."));
    });
  }

  requestResult<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve: (value: T) => void, reject: (reason: unknown) => void) => {
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => reject(request.error ?? new Error("IndexedDB request failed."));
    });
  }

  abortTransaction(transaction: IDBTransaction): void {
    try {
      transaction.abort();
    } catch {
      // Preserve the original operation error when the transaction is already inactive.
    }
  }

  private async openAndPrepareDatabase(): Promise<IDBDatabase> {
    const db = await new Promise<IDBDatabase>((resolve: (value: IDBDatabase) => void, reject: (reason: unknown) => void) => {
      const request = indexedDB.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);

      request.onupgradeneeded = (): void => {
        const upgradedDb = request.result;
        if (!upgradedDb.objectStoreNames.contains(OST_CREATION_IMAGE_STORE)) {
          upgradedDb.createObjectStore(OST_CREATION_IMAGE_STORE, { keyPath: "name" });
        }
        if (!upgradedDb.objectStoreNames.contains(LEGACY_UPLOAD_PROGRESS_STORE)) {
          // Keep the version-2 schema compatible; this store is cleared and no longer used.
          upgradedDb.createObjectStore(LEGACY_UPLOAD_PROGRESS_STORE, { keyPath: "ostNum" });
        }
        Object.values(OST_UPLOAD_STORES).forEach((storeName: string): void => {
          if (!upgradedDb.objectStoreNames.contains(storeName)) {
            upgradedDb.createObjectStore(storeName, { keyPath: "id" });
          }
        });
      };

      request.onsuccess = (): void => {
        const openedDb = request.result;
        openedDb.onversionchange = (): void => {
          openedDb.close();
          this.databasePromise = undefined;
        };
        resolve(openedDb);
      };

      request.onerror = (): void => {
        console.error(`Failed to open IndexedDB "${INDEXED_DB_NAME}":`, request.error);
        reject(request.error ?? new Error(`Failed to open IndexedDB "${INDEXED_DB_NAME}".`));
      };

      request.onblocked = (): void => {
        console.warn("IndexedDB is blocked. Please close other tabs using this database.");
        reject(new Error("IndexedDB is blocked by another open tab."));
      };
    });

    await this.clearLegacyUploadProgress(db);
    return db;
  }

  private async clearLegacyUploadProgress(db: IDBDatabase): Promise<void> {
    if (!db.objectStoreNames.contains(LEGACY_UPLOAD_PROGRESS_STORE)) return;

    const transaction = db.transaction([LEGACY_UPLOAD_PROGRESS_STORE], "readwrite");
    const completed = this.waitForTransaction(transaction);
    transaction.objectStore(LEGACY_UPLOAD_PROGRESS_STORE).clear();
    try {
      await completed;
    } catch (error) {
      console.warn("Could not clear legacy upload-progress records.", error);
    }
  }
}
